import React from "react";
import { EmptyState, LoadingSpinner } from "../../../design-system";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";

const MessageList = ({
  messages,
  selectedMessage,
  onMessageSelect,
  loading,
  error,
  selectedMessages,
  setSelectedMessages,
}) => {
  const handleCheckboxChange = (messageId) => {
    const newSelected = new Set(selectedMessages);
    if (newSelected.has(messageId)) {
      newSelected.delete(messageId);
    } else {
      newSelected.add(messageId);
    }
    setSelectedMessages(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedMessages.size === messages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(messages.map(msg => msg.id)));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <EmptyState
          icon={
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          }
          title="Error loading messages"
          description={error}
        />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <EmptyState
          icon={
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661Z"/>
            </svg>
          }
          title="No messages match your filters"
          description="Try clearing or adjusting your filters to see more messages."
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with select all */}
      <div className={`p-4 border-b ${borders.default} ${surfaces.secondary}`}>
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={messages.length > 0 && selectedMessages.size === messages.length}
            onChange={handleSelectAll}
            className="rounded text-blue-600 dark:text-blue-500 border-gray-300 dark:border-dark-600 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
          <span className={`${typography.label} ${textColors.primary}`}>
            {selectedMessages.size > 0 ? `${selectedMessages.size} selected` : 'Select all'}
          </span>
        </div>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto">
        {messages.map((message) => {
          const isSelected = selectedMessage?.id === message.id;
          const isUnread = !message.is_read;
          const isSystem = message.kind === "system";
          
          return (
            <div
              key={message.id}
              className={`p-4 cursor-pointer transition-colors border-b ${borders.light} ${
                isSelected
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-600 dark:border-l-blue-400'
                  : isUnread
                  ? `bg-blue-50 dark:bg-blue-900/20 ${surfaces.hover}`
                  : `${surfaces.primary} ${surfaces.hover}`
              }`}
              onClick={() => onMessageSelect(message)}
            >
              <div className="flex items-start space-x-3">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedMessages.has(message.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleCheckboxChange(message.id);
                  }}
                  className="mt-1 rounded text-blue-600 dark:text-blue-500 border-gray-300 dark:border-dark-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                />

              {/* Message content */}
              <div className="flex-1 min-w-0">
                {/* Sender info */}
                <div className="flex items-start justify-between gap-3">
                  <div
                    className={`${typography.body} ${textColors.primary} ${!message.is_read ? "font-bold" : "font-normal"} min-w-0`}
                  >
                    <span className="block truncate">{message.sender_display_name}</span>
                  </div>

                  <div className="flex flex-col items-end flex-shrink-0">
                    {isSystem && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        MyVitae
                      </span>
                    )}
                    <div className={`${typography.caption} ${textColors.muted} ${isSystem ? "mt-1" : ""}`}>
                      {formatDate(message.created_at)}
                    </div>
                  </div>
                </div>

                {/* Email */}
                {message.sender_email && (
                  <div className={`${typography.bodySmall} ${textColors.secondary} truncate`}>
                    {message.sender_email}
                  </div>
                )}

                {/* Website */}
                <div className={`${typography.caption} ${textColors.muted} truncate`}>
                  {message.website_container_name || (isSystem ? "MyVitae" : "")}
                </div>

                {/* Message preview */}
                <div className="flex items-center justify-between mt-1">
                  <div className={`${typography.bodySmall} ${textColors.primary} flex-1 pr-2 ${!message.is_read ? 'font-bold' : 'font-normal'}`}>
                    <div className="line-clamp-2">
                      {message.message}
                    </div>
                  </div>
                  
                  {/* Unread indicator */}
                  {!message.is_read && (
                    <div className="w-3 h-3 rounded-full flex-shrink-0 bg-blue-600 dark:bg-blue-400"></div>
                  )}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default MessageList; 